'use client';

import { type FC, useState, useMemo } from 'react';
import { XIcon } from 'lucide-react';

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@ui/select.tsx';
import { useTranslation } from 'react-i18next';

export type Option = {
  id: number | string;
  name: string;
};

type SelectTagInputProps = {
  value: Option[];
  onChange: (newTags: Option[]) => void;
  options: Option[];
  placeholder?: string;
};

const SelectTagInput: FC<SelectTagInputProps> = ({ value, onChange, options, placeholder }) => {
  const { t } = useTranslation();
  const [selectValue, setSelectValue] = useState('');

  const handleChange = (optionId: string) => {
    const option = options.find((opt) => opt.id.toString() === optionId);

    if (option && !value.some((v) => v.id.toString() === option.id.toString())) {
      onChange([...value, option]);
    }

    setSelectValue('');
  };

  const handleRemoveTag = (optionId: number | string) => {
    onChange(value.filter((option) => option.id.toString() !== optionId.toString()));
  };

  const filteredOptions = useMemo(
    () => options.filter((option) => !value.some((v) => v.id.toString() === option.id.toString())),
    [options, value]
  );

  const placeholderText = placeholder ? t(placeholder) : t('admin.kpi.filter.select.tag.label');

  return (
    <div className="flex flex-nowrap w-fit">
      <div className={`bg-muted rounded-l-full space-x-1 flex ${value.length > 0 ? 'p-1' : ''}`}>
        {value.map((option) => {
          return (
            <div
              key={option.id}
              className="py-1.5 px-3 bg-primary text-background cursor-pointer rounded-full text-sm font-light inline-flex items-center justify-center whitespace-nowrap"
            >
              {option.name}
              <XIcon
                className="ml-2 w-4 h-4 transition ease-in-out cursor-pointer text-background hover:text-destructive"
                onClick={() => handleRemoveTag(option.id)}
              />
            </div>
          );
        })}
      </div>

      <div
        className={`py-1 pl-3 max-h-[40px] flex items-center justify-end bg-muted focus:outline-none text-foreground/50 hover:bg-muted-foreground/20 hover:text-foreground ${value.length > 0 ? 'rounded-r-full' : 'rounded-full'}`}
      >
        <Select value={selectValue} onValueChange={handleChange}>
          <SelectTrigger className="min-w-[8rem] space-x-2.5 bg-transparent border-0 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-foreground/50 text-sm font-light whitespace-nowrap hover:text-foreground transition ease-in-out">
            <SelectValue placeholder={placeholderText} />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {filteredOptions.map((option) => (
                <SelectItem key={option.id} value={option.id.toString()} className="cursor-pointer">
                  {option.name}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export { SelectTagInput };
